import {
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import styled from 'styled-components';
import { clamp } from 'lodash';
import {
  easingTokens,
  shadows,
  transitionTokens,
  zIndex,
} from '@ardoq/design-tokens';
import { insetFocusRingCSS } from '@ardoq/style-helpers';
import { SidePanelColumnContext } from './SidePanelColumnContext';
import { useReturnFocusOnHide } from './useReturnFocusOnHide';
import { type WidthDragResize, useWidthDrag } from './useWidthDrag';

const SLIDE_IN_DURATION = '0.4s';
const KEYBOARD_RESIZE_STEP = 16;

const toTransition = (isOpen: boolean, isInstant: boolean): string => {
  if (isInstant) return 'none';
  const width = isOpen
    ? `${SLIDE_IN_DURATION} ${easingTokens.cubicOut}`
    : `${transitionTokens.medium} ${easingTokens.cubicInOut}`;
  return `width ${width}, visibility ${transitionTokens.medium}`;
};

const Panel = styled.div<{
  $isOpen: boolean;
  $isInstant: boolean;
  $isDragging: boolean;
}>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  overflow: hidden;
  z-index: ${zIndex.ABOVE_GRID_EDITOR_BACKGROUND};
  box-shadow: ${shadows.shadowM};
  clip-path: inset(0 0 0 -100vw);
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: ${({ $isOpen, $isInstant }) => toTransition($isOpen, $isInstant)};
  ${({ $isDragging }) => $isDragging && 'pointer-events: none;'}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Content = styled.div`
  flex-shrink: 0;
  height: 100%;
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 1;

  &:focus-visible {
    outline: none;
    ${insetFocusRingCSS}
  }
`;

export type SidePanelResize = WidthDragResize & {
  label: string;
};

const KEYBOARD_RESIZE_DIRECTION: Partial<Record<string, number>> = {
  ArrowLeft: 1,
  ArrowRight: -1,
};

const resizeWithKeyboard = (
  event: KeyboardEvent<HTMLDivElement>,
  width: number,
  { minWidth, maxWidth, onResizeEnd }: SidePanelResize
): void => {
  const direction = KEYBOARD_RESIZE_DIRECTION[event.key];
  if (direction === undefined) return;
  event.preventDefault();
  onResizeEnd(
    clamp(width + direction * KEYBOARD_RESIZE_STEP, minWidth, maxWidth)
  );
};

export type SidePanelProps = {
  /**
   * Identifies the panel within a `SidePanelColumnContext`. `null` for a panel
   * rendered outside a column, which opens whenever `isOpen` is true.
   */
  columnPanelId: string | null;
  isOpen: boolean;
  width: number;
  children: ReactNode;
  /** Called on Escape anywhere on the page while the panel is open. */
  onClose?: VoidFunction;
  resize?: SidePanelResize;
};

/** A right-aligned panel that slides in at a fixed content width. */
export const SidePanel = ({
  columnPanelId,
  isOpen: isRequestedOpen,
  width,
  children,
  onClose,
  resize,
}: SidePanelProps) => {
  const column = useContext(SidePanelColumnContext);
  const isOpen =
    isRequestedOpen &&
    (column === null || column.activePanelId === columnPanelId);
  const isAnimated = column?.isAnimated ?? true;
  const panelRef = useRef<HTMLDivElement>(null);
  const { dragWidth, isDragging, startDrag } = useWidthDrag({
    panelRef,
    alignment: 'right',
    resize,
  });
  const contentWidth = dragWidth ?? width;

  const onCloseRef = useRef(onClose);
  const isClosable = Boolean(onClose);

  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });
  useReturnFocusOnHide(!isOpen, panelRef);
  useEffect(() => {
    if (!isOpen || !isClosable) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onCloseRef.current?.();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, isClosable]);

  return (
    <Panel
      ref={panelRef}
      style={{ width: isOpen ? contentWidth : 0 }}
      $isOpen={isOpen}
      $isInstant={!isAnimated || isDragging}
      $isDragging={isDragging}
    >
      <Content style={{ width: contentWidth }}>{children}</Content>
      {resize && isOpen && (
        <ResizeHandle
          role="separator"
          tabIndex={0}
          aria-label={resize.label}
          aria-orientation="vertical"
          aria-valuenow={contentWidth}
          aria-valuemin={resize.minWidth}
          aria-valuemax={resize.maxWidth}
          data-test-id="side-panel-resize-handle"
          onMouseDown={startDrag}
          onKeyDown={event => resizeWithKeyboard(event, width, resize)}
        />
      )}
    </Panel>
  );
};
